import React, { useEffect, useState, useRef } from 'react';
import { Select, message, Spin, Input, Row, Col, Table } from 'antd';
import { routes, RouteItem } from '../../../routes/index';
import { insertData, updateData } from '../../../services/api';
import { useGetDestinationFolder } from '../../../hooks/genericHooks';
import { CorDestinationFolder } from '../../../types';
import StandardTable from '../../../components/common/StandardTable';
import { useAuth } from '../../../components/common/security/AuthContext';
import { dccList, dccUpload } from '../../../services/dccApi';
import Button from '../../../components/common/Button';

// Recursively flatten the routes tree (supports children and grandchildren)
function getAllRoutes(obj: Record<string, RouteItem>): { page: string, path: string }[] {
  const out: { page: string, path: string }[] = [];
  const visit = (node: any) => {
    if (!node) return;
    if (node.element && node.path) {
      const pageName = node.label || String(node.path).split('/').pop() || node.path;
      out.push({ page: pageName, path: node.path });
    }
    if (node.children) {
      Object.values(node.children).forEach(visit);
    }
    if (node.grandchildren) {
      Object.values(node.grandchildren).forEach(visit);
    }
  };
  Object.values(obj).forEach(visit);
  return out;
}

function cleanPath(path: string): string {
  // Normalize any dynamic trailing param like /:id, /:recordid, /:anything
  let p = path.replace(/\/(:(id|recordid|recorid|\w*id|\w+))$/i, '');
  // Remove any trailing slash leftovers
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

const CoreFolderAssignment: React.FC = () => {
  const { token } = useAuth();
  const [subfolders, setSubfolders] = useState<string[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [initialMappings, setInitialMappings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [filteredPages, setFilteredPages] = useState<{ page: string, path: string }[]>([]);
  const [pageFilter, setPageFilter] = useState('');
  const [routeFilter, setRouteFilter] = useState('');
  const { data: existingFolders, refetch } = useGetDestinationFolder();
  const didInit = useRef(false);

  // Bulk upload state (to remote /root/uploads server)
  const [uploadRoot, setUploadRoot] = useState<string>('');
  const [uploadSubpath, setUploadSubpath] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Dynamically extract filteredPages after module initialization to avoid circular import issues
    const allPages = getAllRoutes(routes);
    const filtered = allPages.filter(
      p => /create|edit|fill|request/i.test(p.page) || /\/create|\/edit|\/fill|\/request/i.test(p.path)
    ).map(p => ({ ...p, path: cleanPath(p.path) }));
    setFilteredPages(filtered);

    setLoading(true);
    dccList('', token)
      .then((data) => {
        if (Array.isArray(data)) {
          // Extract unique top-level folder names from all paths
          const topFolders = Array.from(new Set(
            data
              .filter(f => typeof f === 'string')
              .map(f => {
                // Remove leading backslash if present
                let clean = f.startsWith('\\') ? f.slice(1) : f;
                // Split by backslash and take the first segment
                return clean.split('\\')[0];
              })
              .filter(Boolean)
          ));
          setSubfolders(topFolders);
        } else setSubfolders([]);
      })
      .catch(() => setSubfolders([]))
      .finally(() => setLoading(false));
  }, [token]);

  // Load initial folder mappings from cor_destination_folders
  useEffect(() => {
    if (!didInit.current && existingFolders?.data) {
      // Map: route_path -> folder_path
      const dbMappings: Record<string, string> = {};
      for (const row of existingFolders.data) {
        if (row.route_path && row.folder_path) {
          dbMappings[row.route_path] = row.folder_path;
        }
      }
      setMappings(dbMappings);
      setInitialMappings(dbMappings);
      didInit.current = true;
    }
  }, [existingFolders]);

  const handleChange = (path: string, folder: string) => {
    setMappings(prev => ({ ...prev, [path]: folder }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Refresh data before processing changes to avoid stale data issues
      if (refetch) {
        await refetch();
      }

      for (const [route_path, folder_path] of Object.entries(mappings)) {
        if (initialMappings[route_path] === folder_path) continue;
        const existing = existingFolders?.data?.find((f: CorDestinationFolder) => f.route_path === route_path);
        const page_name = filteredPages.find(p => p.path === route_path)?.page || '';
        if (existing) {
          const { created_at, created_by, updated_at, updated_by, ...rest } = existing;
          await updateData(
            'cor_destination_folders',
            existing.id,
            { ...rest, folder_path, updated_at: new Date().toISOString() }
          );
        } else {
          await insertData('cor_destination_folders', {
            route_path,
            folder_path,
            page_name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
      }
      message.success('Folder mappings saved!');
      if (refetch) {
        const refreshed = await refetch();
        const fresh = (refreshed as any)?.data?.data || (refreshed as any)?.data || existingFolders?.data || [];
        if (Array.isArray(fresh)) {
          const dbMappings: Record<string, string> = {};
          for (const row of fresh) {
            if (row && row.route_path && row.folder_path) {
              dbMappings[row.route_path] = row.folder_path;
            }
          }
          setMappings(dbMappings);
          setInitialMappings(dbMappings);
        }
      }
    } catch (err) {
      message.error('Failed to save folder mappings');
    } finally {
      setLoading(false);
    }
  };

  const buildUploadPath = () => {
    const root = (uploadRoot || '').trim();
    let sub = uploadSubpath.trim();
    // Normalize slashes and trim
    sub = sub.replace(/^[\\/]+|[\\/]+$/g, '');
    return [root, sub].filter(Boolean).join('/');
  };

  const handleBulkUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      message.warning('Please select at least one file to upload.');
      return;
    }
    const targetPath = buildUploadPath();
    setIsUploading(true);

    try {
      const data = await dccUpload(targetPath, Array.from(files), token);
      const uploadedCount = Array.isArray((data as any)?.uploaded) ? (data as any).uploaded.length : files.length;
      message.success(`Uploaded ${uploadedCount} file(s) to /root/uploads/${targetPath || ''}`);
    } catch (e) {
      message.error('Bulk upload failed (network error).');
    } finally {
      setIsUploading(false);
      if (uploadInputRef.current) {
        uploadInputRef.current.value = '';
      }
    }
  };

  // Filtered data based on input boxes
  const dataToShow = filteredPages.filter(row =>
    row.page.toLowerCase().includes(pageFilter.toLowerCase()) &&
    row.path.toLowerCase().includes(routeFilter.toLowerCase())
  );

  return (
    <Spin spinning={loading}>
      <div style={{ padding: 8 }}>
        <h2 style={{ marginBottom: 8 }}>Folder Assignments</h2>
        <Row gutter={8} style={{ marginBottom: 12 }}>
          <Col>
            <Input
              placeholder="Filter by Page"
              value={pageFilter}
              allowClear
              onChange={e => setPageFilter(e.target.value)}
              style={{ width: 200 }}
              size="small"
            />
          </Col>
          <Col>
            <Input
              placeholder="Filter by Route Path"
              value={routeFilter}
              allowClear
              onChange={e => setRouteFilter(e.target.value)}
              style={{ width: 300 }}
              size="small"
            />
          </Col>
        </Row>
        <Table
          size="small"
          dataSource={dataToShow}
          rowKey="path"
          pagination={false}
          style={{ margin: 0 }}
          columns={[
            {
              title: 'Page',
              dataIndex: 'page',
              key: 'page',
              width: 80,
              sorter: (a, b) => a.page.localeCompare(b.page),
            },
            {
              title: 'Route Path',
              dataIndex: 'path',
              key: 'path',
              width: 260,
              sorter: (a, b) => a.path.localeCompare(b.path),
            },
            {
              title: 'Destination Subfolder',
              key: 'folder',
              width: 180,
              render: (_: any, record: any) => (
                <Select
                  size="small"
                  style={{ width: 320 }}
                  allowClear
                  value={mappings[record.path] || undefined}
                  onChange={folder => handleChange(record.path, folder)}
                  placeholder="Select subfolder"
                >
                  <Select.Option value="" key="__empty__">(None)</Select.Option>
                  {Array.isArray(subfolders) && subfolders.map(f => (
                    <Select.Option key={f} value={f}>{f}</Select.Option>
                  ))}
                </Select>
              ),
            },
          ]}
          bordered
        />
        <Button action="edit" type="primary" size="small" style={{ marginTop: 8, float: 'right' }} onClick={handleSave}>
          Save Mappings
        </Button>
        <div style={{ clear: 'both' }} />

        {/* Bulk upload panel */}
        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <h3 style={{ marginBottom: 8 }}>Bulk Upload to /root/uploads</h3>
          <Row gutter={8} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Root folder (e.g. hse_inspections)"
                value={uploadRoot}
                onChange={(e) => setUploadRoot(e.target.value)}
                size="small"
              />
            </Col>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Subpath (optional, e.g. HSE-MI-00079/forms)"
                value={uploadSubpath}
                onChange={(e) => setUploadSubpath(e.target.value)}
                size="small"
              />
            </Col>
            <Col xs={24} sm={8}>
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                style={{ marginBottom: 8 }}
                onChange={(e) => handleBulkUpload(e.target.files)}
              />
              <div style={{ fontSize: 12, color: '#999' }}>
                Files will be uploaded to: <code>/root/uploads/{buildUploadPath() || '(root)'}</code>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Spin>
  );
};

export default CoreFolderAssignment;
